import type { Booking } from '../api/bookings';
import type { Customer } from '../api/masters';
import { toDayjs } from '../format/dates';
import { formatAmount } from '../format/money';
import type { IdDocumentType } from '../generated/enums';

/**
 * What the phone's Customers screens say about a customer (`docs/Mobile_Redesign_Plan.md`, step 11)
 * — the row, the three figures, the identity line, and the links to call and to WhatsApp.
 */

/** "first booking", "rented once", "rented twice", "rented 4 times". */
export function rentedWords(count: number): string {
  if (count <= 0) {
    return 'first booking';
  }

  if (count === 1) {
    return 'rented once';
  }

  return count === 2 ? 'rented twice' : `rented ${count} times`;
}

/** "98250 12345" — a ten-digit Indian mobile as it is said aloud; anything else as stored. */
export function mobileWords(mobile: string): string {
  return /^\d{10}$/.test(mobile) ? `${mobile.slice(0, 5)} ${mobile.slice(5)}` : mobile;
}

/** "98250 12345 · rented 4 times" — a customer's row. */
export function customerLine(customer: Pick<Customer, 'mobile' | 'rentalCount'>): string {
  return `${mobileWords(customer.mobile)} · ${rentedWords(customer.rentalCount)}`;
}

/**
 * The three figures on a customer's page: rentals, spend and when they last rented — "Sep" this
 * year, "Sep 2025" before it. Spend is null for a role the server withholds money from, and then the
 * figure is not drawn rather than drawn as nothing spent.
 */
export function customerFigures(
  customer: Pick<Customer, 'rentalCount' | 'totalSpend' | 'lastRentedOn'>,
  today: string,
  currency: string,
): { value: string; label: string }[] {
  const last = toDayjs(customer.lastRentedOn ?? undefined);
  const thisYear = toDayjs(today)?.year();

  return [
    {
      value: String(customer.rentalCount),
      label: customer.rentalCount === 1 ? 'rental' : 'rentals',
    },
    ...(customer.totalSpend === null
      ? []
      : [{ value: `${currency}${formatAmount(customer.totalSpend)}`, label: 'spent' }]),
    {
      value: last ? last.format(last.year() === thisYear ? 'MMM' : 'MMM YYYY') : 'Never',
      label: 'last rented',
    },
  ];
}

export const idDocumentLabel: Record<IdDocumentType, string> = {
  Aadhaar: 'Aadhaar',
  Pan: 'PAN',
  DrivingLicence: 'Driving licence',
  Passport: 'Passport',
  VoterId: 'Voter ID',
  Other: 'ID',
};

/**
 * "Aadhaar ending 4321", with "Expired" or "Scan on file" under it — never the whole number, which
 * has no business on a phone's screen across a counter. Null when no document is on file.
 */
export function idLine(
  customer: Pick<
    Customer,
    'idDocumentType' | 'idDocumentNumber' | 'idDocumentIsExpired' | 'idDocumentScanUrl'
  >,
): { title: string; note: string | null } | null {
  if (!customer.idDocumentType) {
    return null;
  }

  const digits = (customer.idDocumentNumber ?? '').replace(/\s+/g, '');
  const label = idDocumentLabel[customer.idDocumentType];

  return {
    title: digits.length >= 4 ? `${label} ending ${digits.slice(-4)}` : label,
    note: customer.idDocumentIsExpired
      ? 'Expired'
      : customer.idDocumentScanUrl
        ? 'Scan on file'
        : null,
  };
}

/**
 * WhatsApp's link to a chat with this number. A ten-digit number is an Indian mobile, the way it is
 * stored (`LoginIdentifier.NormalizeMobile` strips the +91), so the country code goes back on.
 * With `text`, the chat opens with the message typed in — the person still presses send.
 */
export function whatsappLink(mobile: string, text?: string): string {
  const digits = mobile.replace(/\D+/g, '');
  const chat = `https://wa.me/${digits.length === 10 ? `91${digits}` : digits}`;

  return text ? `${chat}?text=${encodeURIComponent(text)}` : chat;
}

/**
 * One of a customer's bookings on their page: "Wedding · Sat 24 Oct" over "BK-2026-27-0031 · 3
 * garments". A booking with no event named is a Booking; one with no number yet says so.
 */
export function customerBookingRow(
  booking: Pick<
    Booking,
    | 'eventKind'
    | 'eventDate'
    | 'chargeFromDate'
    | 'documentNumber'
    | 'quotationNumber'
    | 'garmentCount'
  >,
): { title: string; line: string } {
  const day = toDayjs(booking.eventDate ?? booking.chargeFromDate);
  const number = booking.documentNumber || booking.quotationNumber || 'No number yet';
  const count = booking.garmentCount;

  return {
    title: `${booking.eventKind?.trim() || 'Booking'}${day ? ` · ${day.format('ddd D MMM')}` : ''}`,
    line: `${number} · ${count} garment${count === 1 ? '' : 's'}`,
  };
}

/** "Hiren" — the first name, for "New booking for Hiren". */
export function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? name;
}
